import { ApplicationIndividual } from '../domain/application-parties.domain';
import { normalizeDriversLicenceNumber } from './identification-number';

/**
 * The parts of an individual an Equifax enquiry is built and matched on. Structural and fully
 * optional so the API-shaped individuals, the non-production stub identities and a bare `{}`
 * lookup all satisfy it.
 */
export type EquifaxEnquiryIndividual = Partial<Pick<
  ApplicationIndividual,
  'givenName' | 'middleName' | 'surname' | 'dateOfBirth' | 'identification'
>>;

// The driver's licence value in constants.idTypes, compared with punctuation and case stripped so
// records saved as 'driverslicence' or "Driver's Licence" resolve the same way.
const DRIVERS_LICENCE_ID_TYPE = 'driverslicence';

/**
 * The individual's driver's licence number, or null when the captured identity document is
 * something else or nothing at all. Passports are deliberately excluded rather than passed through:
 * Equifax checks a licence number against its own licence data, so a passport number in that slot
 * is a guaranteed mismatch and would degrade the match rather than improve it.
 */
export const getIndividualDriversLicenceNumber = (individual: EquifaxEnquiryIndividual): string | null => {
  const idType = (individual.identification?.type ?? '').toLowerCase().replace(/[^a-z]/g, '');
  if (idType !== DRIVERS_LICENCE_ID_TYPE) {
    return null;
  }
  return normalizeDriversLicenceNumber(individual.identification?.number) || null;
};

/** Normalisation applied to every part of the bureau cache key, matching the existing name columns. */
export const normalizeEquifaxIdentityValue = (value: string | null | undefined): string =>
  (value ?? '').trim().toUpperCase();
